import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Lock, Check, Shield, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResetPassword = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token");
  
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fromLogin = location.state?.fromLogin;
  const isTokenReset = !!resetToken;

  const passwordRequirements = [
    { label: "At least 8 characters", met: newPassword.length >= 8 },
    { label: "Contains a number", met: /\d/.test(newPassword) },
    { label: "Contains uppercase letter", met: /[A-Z]/.test(newPassword) },
    { label: "Passwords match", met: newPassword === confirmPassword && newPassword.length > 0 },
  ];

  const allRequirementsMet = passwordRequirements.every(req => req.met);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (!allRequirementsMet) {
      setError("Please meet all password requirements");
      return;
    }

    setLoading(true);

    try {
      if (isTokenReset) {
        // Token-based reset (from forgot password email)
        await axios.post(`${API}/auth/reset-password-token`, 
          { token: resetToken, new_password: newPassword }
        );
        toast.success("Password reset successful!", {
          description: "You can now sign in with your new password."
        });
        navigate("/login");
      } else {
        // Authenticated reset (first login or manual reset)
        await axios.post(`${API}/auth/beta/reset-password`, 
          { current_password: currentPassword, new_password: newPassword },
          { withCredentials: true }
        );
        toast.success("Password updated!", {
          description: "You can now use your new password to sign in."
        });
        navigate("/progress");
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to reset password. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-clarity-bg relative overflow-hidden flex items-center justify-center p-6">
      {/* Background pattern */}
      <div className="absolute inset-0 grid-pattern opacity-50"></div>
      
      {/* Ambient glow */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_clarity-wellness-log/artifacts/dv3nbk4x_Logo%20new%20copy.jpg" 
              alt="ClarityOS" 
              className="w-12 h-12 rounded-xl object-cover"
            />
            <span className="text-3xl tracking-tight" style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
              <span className="font-bold text-white">Clarity</span>
              <span className="font-normal text-brand-primary">OS</span>
            </span>
          </div>
        </div>

        {/* Reset Password Card */}
        <div className="glass rounded-2xl p-8">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 rounded-xl bg-brand-primary/10">
            <Shield className="w-8 h-8 text-brand-primary" />
          </div>
          
          <h1 className="font-heading text-2xl font-bold text-white text-center mb-2">
            {isTokenReset ? "Reset Your Password" : fromLogin ? "Set Your Password" : "Reset Password"}
          </h1>
          <p className="text-slate-400 text-center mb-8">
            {isTokenReset 
              ? "Create a new secure password for your account"
              : fromLogin 
                ? "Create a secure password for your account" 
                : "Enter your current password and choose a new one"}
          </p>

          <form onSubmit={handleResetPassword} className="space-y-6">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Only show current password field if not token-based reset */}
            {!isTokenReset && (
              <div className="space-y-2">
                <Label className="text-slate-400">
                  {fromLogin ? "Default Password" : "Current Password"}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type={showPasswords ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                    className="pl-10 bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-600 focus:border-brand-primary"
                    data-testid="current-password-input"
                  />
                </div>
                {fromLogin && (
                  <p className="text-xs text-slate-500">
                    Use the default password provided to you
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-400">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  className="pl-10 bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-600 focus:border-brand-primary"
                  data-testid="new-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="pl-10 pr-10 bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-600 focus:border-brand-primary"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-400"
                >
                  {showPasswords ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="space-y-2 p-4 rounded-lg bg-clarity-subtle/30">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Password Requirements</p>
              {passwordRequirements.map((req, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    req.met ? 'bg-brand-secondary/20' : 'bg-white/5'
                  }`}>
                    {req.met && <Check className="w-3 h-3 text-brand-secondary" />}
                  </div>
                  <span className={`text-sm ${req.met ? 'text-brand-secondary' : 'text-slate-500'}`}>
                    {req.label}
                  </span>
                </div>
              ))}
            </div>

            <Button
              type="submit"
              disabled={loading || !allRequirementsMet}
              className="w-full bg-brand-primary text-clarity-bg font-bold py-3 rounded-lg hover:bg-yellow-400 hover:shadow-[0_0_20px_rgba(229,184,76,0.4)] transition-all disabled:opacity-50"
              data-testid="reset-password-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-clarity-bg border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </span>
              ) : (
                "Set New Password"
              )}
            </Button>
          </form>

          {/* Back to login link for token-based reset */}
          {isTokenReset && (
            <button
              onClick={() => navigate("/login")}
              className="w-full mt-4 flex items-center justify-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
